import React, { useState } from "react";
import * as Mui from "@mui/material";
import {
  AccessTimeRounded,
  ArrowForwardRounded,
  FactCheckRounded,
  HolidayVillageRounded,
  MoreHorizRounded,
  ScheduleRounded,
} from "@mui/icons-material";
import { designColors } from "services/constants/designTokens";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { ctaButtonSx, sectionCardSx, SectionHeader } from "./homeUi";

// Reads one category's pending count out of the
// team_zone/overall_count/approval-counts/ response (the same endpoint the
// Approval Center itself uses — see TeamZone/Approve/index.js's toCountSet /
// categoryCountMetrics). Overtime and Off Day Duty nest their totals one
// level deeper under `.overall`, everything else sits directly on the
// category key — mirroring that same quirk here rather than reshaping the
// response.
const categoryPendingCount = (categories, key, nested = false) => {
  const entry = nested ? categories?.[key]?.overall : categories?.[key];
  return entry?.pending_counts ?? 0;
};

const buildPendingApprovalItems = (counts, categoryCounts) => {
  const leaveCount =
    categoryPendingCount(categoryCounts?.categories, "leave_request") ||
    counts?.pending_leave_reques_counts ||
    0;
  const overtimeCount =
    categoryPendingCount(categoryCounts?.categories, "overtime_request", true) ||
    counts?.peding_over_time_counts ||
    0;
  const permissionCount = categoryPendingCount(
    categoryCounts?.categories,
    "permission",
  );
  const totalCount =
    categoryCounts?.overall_pending_counts ?? counts?.pending_approval_counts ?? 0;
  // The grand total the backend returns isn't broken down by category on
  // this endpoint — Comp Off, On Duty, Break, Advance Salary, Expense, Off
  // Day Duty (whatever isn't already its own row above) all fold into it
  // without a field of their own. Rather than lose that (a real pending
  // item in one of those categories used to just vanish once the old "All
  // Pending Approvals" total row was removed), back it out as a remainder:
  // whatever the total counts that Leave/Overtime/Permission don't already
  // account for. Clamped at 0 so a stale/mismatched total never shows a
  // confusing negative count.
  const otherCount = Math.max(
    totalCount - leaveCount - overtimeCount - permissionCount,
    0,
  );

  return [
    // The headline number — same raw pending_approval_counts the card used
    // to show before it was removed, brought back per request. No
    // accessKey: like Other Pending Approvals below, it isn't tied to one
    // screen's permission, so it shows for anyone who can see this card at
    // all. Together with the three named rows and the Other Pending
    // Approvals remainder below, it's the total this whole list breaks
    // down into — Leave + Overtime + Permission + Other always sums back
    // to this number by construction.
    {
      title: "All Pending Approvals",
      subtitle: `${totalCount} total items need action`,
      count: totalCount,
      icon: ScheduleRounded,
      color: "#10B981",
      navigationTarget: { moduleName: "Approvals" },
    },
    {
      title: "Leave Requests",
      accessKey: "leave",
      subtitle: `${leaveCount} requests awaiting approval`,
      count: leaveCount,
      icon: HolidayVillageRounded,
      color: "#EF4444",
      navigationTarget: { moduleName: "Approvals", search: "view=leave" },
    },
    {
      title: "Overtime Requests",
      accessKey: "overtime",
      subtitle: `${overtimeCount} requests awaiting approval`,
      count: overtimeCount,
      icon: AccessTimeRounded,
      color: "#F97316",
      navigationTarget: { moduleName: "Approvals", search: "view=overtime" },
    },
    {
      title: "Permission Requests",
      accessKey: "permission",
      subtitle: `${permissionCount} requests awaiting approval`,
      count: permissionCount,
      icon: FactCheckRounded,
      color: "#8B5CF6",
      navigationTarget: { moduleName: "Approvals", search: "view=permission" },
    },
    // No accessKey — unlike the rows above, this one isn't tied to a single
    // approval screen's permission. It shows for anyone who can see this
    // card at all, as long as there's actually something to report, so a
    // pending item sitting in a category with no row of its own (Comp Off,
    // On Duty, Break, Advance Salary, Expense, Off Day Duty) never goes
    // completely invisible just because the viewer's access is partial.
    // What happens on click depends on access — see isRemainderRow below.
    {
      title: "Other Pending Approvals",
      isRemainderRow: true,
      subtitle: `${otherCount} requests awaiting approval`,
      count: otherCount,
      icon: MoreHorizRounded,
      color: "#6B7280",
    },
  ].filter((item) => !item.isRemainderRow || item.count > 0);
};

// Full per-category breakdown shown in the "View all" side panel — every
// request type the Approval Center itself lists, not just the two/three
// this card breaks out as their own row. Attendance Correction and Shift
// Change are deliberately left out: the approval-counts endpoint doesn't
// report a pending share for either yet (same gap noted in
// TeamZone/Approve/index.js's categoryCountMetrics), so showing them here
// would read as "0 pending" rather than "not tracked".
const PENDING_CATEGORY_BREAKDOWN = [
  { title: "Leave Requests", key: "leave_request", view: "leave", icon: HolidayVillageRounded, color: "#DC2626" },
  { title: "Overtime Requests", key: "overtime_request", nested: true, view: "overtime", icon: AccessTimeRounded, color: "#C2410C" },
  { title: "Permission Requests", key: "permission", view: "permission", icon: FactCheckRounded, color: "#7C3AED" },
  { title: "Off Day Duty", key: "off_day_duty", nested: true, view: "offday-duty", icon: HolidayVillageRounded, color: "#0369A1" },
  { title: "On Duty", key: "on_duty", view: "onduty", icon: ScheduleRounded, color: "#0F766E" },
  { title: "Comp Off", key: "comp_off", view: "compoff", icon: ScheduleRounded, color: "#4338CA" },
  { title: "Break", key: "break", view: "break", icon: AccessTimeRounded, color: "#B45309" },
  { title: "Advance Salary", key: "salary_advance", view: "advance-salary", icon: FactCheckRounded, color: "#047857" },
  { title: "Reimbursement", key: "expense_claim", view: "expense", icon: FactCheckRounded, color: "#BE185D" },
];

export const PendingApprovalsSection = ({
  approvalAccess = {},
  navigateToModule,
  attendanceCounts,
  approvalCategoryCounts,
  loading = false,
}) => {
  // Only a full-access user gets a breakdown panel worth opening (every
  // category's own count, meaningful to compare) — everyone else still gets
  // "View all"/the Other Pending Approvals row, just routed straight to the
  // Approval Center instead, since a partial per-category breakdown isn't
  // actionable for them.
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const pendingApprovalItems = buildPendingApprovalItems(
    attendanceCounts,
    approvalCategoryCounts,
  ).filter((item) => !item.accessKey || approvalAccess[item.accessKey]);

  if (pendingApprovalItems.length === 0) return null;

  const breakdownRows = PENDING_CATEGORY_BREAKDOWN.map((def) => ({
    ...def,
    count: categoryPendingCount(
      approvalCategoryCounts?.categories,
      def.key,
      def.nested,
    ),
  }));

  const handleViewAll = () => {
    if (approvalAccess.all) {
      setBreakdownOpen(true);
    } else {
      navigateToModule("Approvals");
    }
  };

  const activeCategoryCount = breakdownRows.filter((row) => row.count > 0).length;

  return (
    <Mui.Box
      sx={{
        ...sectionCardSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!loading && (
        <SectionHeader
          title="Pending Approvals"
          tooltip="Pending requests for approval screens you have access to view."
          actionLabel="View all"
          onAction={handleViewAll}
        />
      )}
      <Mui.Stack spacing={0.5} sx={{ flex: 1, pt: 1.25 }}>
        {loading
          ? pendingApprovalItems.map((_, index) => (
              <Mui.Stack
                key={index}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.25}
                sx={{ py: 0.9 }}
              >
                <Mui.Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  <Mui.Skeleton
                    variant="rounded"
                    width={34}
                    height={34}
                    sx={{ borderRadius: 1.5, flexShrink: 0 }}
                  />
                  <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
                    <Mui.Skeleton variant="text" width="52%" height={22} />
                    <Mui.Skeleton variant="text" width="82%" height={18} />
                  </Mui.Box>
                </Mui.Stack>
                <Mui.Stack direction="row" spacing={0.25} alignItems="center">
                  <Mui.Skeleton variant="text" width={18} height={24} />
                  <Mui.Skeleton variant="circular" width={18} height={18} />
                </Mui.Stack>
              </Mui.Stack>
            ))
          : pendingApprovalItems.map((item) => (
              <Mui.Stack
                key={item.title}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.25}
                onClick={() => {
                  if (item.isRemainderRow) {
                    // Full access -> the breakdown panel actually means
                    // something (every category's own count to compare).
                    // Partial access -> just send them to the Approval
                    // Center itself, which already filters what each viewer
                    // can act on by their real submodule permissions.
                    if (approvalAccess.all) {
                      setBreakdownOpen(true);
                    } else {
                      navigateToModule("Approvals");
                    }
                    return;
                  }
                  navigateToModule(
                    item.navigationTarget.moduleName,
                    item.navigationTarget.search,
                  );
                }}
                sx={{
                  py: 0.9,
                  borderBottom: `1px solid ${designColors.slate100}`,
                  cursor: "pointer",
                  borderRadius: 1.5,
                  transition: "background-color 120ms ease",
                  "&:hover": {
                    backgroundColor: designColors.slate50,
                  },
                }}
              >
                <Mui.Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Mui.Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      backgroundColor: `${item.color}12`,
                      color: item.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <item.icon sx={{ fontSize: 18 }} />
                  </Mui.Box>
                  <Mui.Box sx={{ minWidth: 0 }}>
                    <Mui.Typography
                      sx={{
                        fontSize: 14.5,
                        fontWeight: 500,
                        color: designColors.slate900,
                      }}
                    >
                      {item.title}
                    </Mui.Typography>
                    <Mui.Typography
                      sx={{ fontSize: 13, color: designColors.slate600 }}
                    >
                      {item.subtitle}
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.Stack>
                <Mui.Stack direction="row" spacing={0.25} alignItems="center">
                  <Mui.Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: item.color,
                    }}
                  >
                    {item.count}
                  </Mui.Typography>
                  <ArrowForwardRounded
                    sx={{ fontSize: 18, color: designColors.slate400 }}
                  />
                </Mui.Stack>
              </Mui.Stack>
            ))}
      </Mui.Stack>
      {loading ? (
        <Mui.Skeleton
          variant="rounded"
          height={36}
          sx={{ borderRadius: 999, mt: 1.5 }}
        />
      ) : (
        <Mui.Button
          fullWidth
          endIcon={<ArrowForwardRounded />}
          sx={{ ...ctaButtonSx, mt: 1.5 }}
          onClick={() => navigateToModule("Approvals")}
        >
          Go to Approval Center
        </Mui.Button>
      )}

      <GlobalSidePanel
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        title="All Pending Approvals"
        subtitle="Pending requests across every approval type"
        bodySx={{ backgroundColor: designColors.pageBg }}
        footer={
          <Mui.Button
            fullWidth
            endIcon={<ArrowForwardRounded />}
            sx={{ ...ctaButtonSx, minHeight: 44 }}
            onClick={() => {
              setBreakdownOpen(false);
              navigateToModule("Approvals");
            }}
          >
            Go to Approval Center
          </Mui.Button>
        }
      >
        <Mui.Stack spacing={1.25} sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: "auto" }}>
          <Mui.Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ pb: 0.5 }}>
            <Mui.Typography sx={{ fontSize: 14, fontWeight: 600, color: designColors.slate800 }}>
              By approval type
            </Mui.Typography>
            <Mui.Typography sx={{ fontSize: 12, color: designColors.slate600 }}>
              {activeCategoryCount} of {breakdownRows.length} with pending items
            </Mui.Typography>
          </Mui.Stack>
          {breakdownRows.map((row) => (
            <Mui.ButtonBase
              key={row.key}
              aria-label={`${row.title}: ${row.count} pending requests. View requests`}
              onClick={() => {
                setBreakdownOpen(false);
                navigateToModule("Approvals", `view=${row.view}`);
              }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1.5,
                width: "100%",
                p: 1.5,
                borderRadius: 2,
                textAlign: "left",
                backgroundColor: designColors.white,
                border: `1px solid ${designColors.slate200}`,
                transition: "border-color 150ms ease, box-shadow 150ms ease",
                "&:hover": {
                  borderColor: designColors.teal,
                  boxShadow: "0 3px 10px rgba(15, 23, 42, 0.06)",
                },
                "&.Mui-focusVisible": {
                  outline: `2px solid ${designColors.teal}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Mui.Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", color: row.color, backgroundColor: `${row.color}10` }}>
                  <row.icon sx={{ fontSize: 21 }} />
                </Mui.Box>
                <Mui.Box sx={{ minWidth: 0 }}>
                  <Mui.Typography sx={{ fontSize: 14, fontWeight: 600, color: designColors.slate800 }}>
                    {row.title}
                  </Mui.Typography>
                  <Mui.Typography sx={{ mt: 0.25, fontSize: 12, color: designColors.slate600 }}>
                    {row.count > 0 ? "Awaiting approval" : "No pending requests"}
                  </Mui.Typography>
                </Mui.Box>
              </Mui.Stack>
              <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <Mui.Typography
                  component="span"
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    minWidth: 34,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    textAlign: "center",
                    fontVariantNumeric: "tabular-nums",
                    color: row.count > 0 ? row.color : designColors.slate600,
                    backgroundColor: row.count > 0 ? `${row.color}10` : designColors.pageBg,
                  }}
                >
                  {row.count}
                </Mui.Typography>
                <ArrowForwardRounded
                  sx={{ fontSize: 16, color: designColors.slate400 }}
                />
              </Mui.Stack>
            </Mui.ButtonBase>
          ))}
        </Mui.Stack>
      </GlobalSidePanel>
    </Mui.Box>
  );
};
